// Elintra — Governance Attestation Panel | v3.2 | UAG v3.0.0 Phase 2B
// Renders the dual-attestation proof: governance_hash + algorand_tx_id.
// Makes Constitutional Commerce visible — this is the sovereignty artifact.

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ExternalLink, ShieldCheck } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { useAppStore, useAllQueries } from '@/store/appStore';

export function GovernanceAttestationPanel() {
  const x402PaymentCount = useAppStore((s) => s.x402PaymentCount);
  const algoSettledUsdt  = useAppStore((s) => s.algoSettledUsdt);
  const allQueries       = useAllQueries();

  // Find the most recent query with a dual-attested algorand_tx_id
  const latestAttestation = useMemo(
    () => allQueries.find((q) => q.algorand_tx_id && q.governanceHash),
    [allQueries],
  );

  const hasliveAttestation = !!latestAttestation;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <Card className="border-emerald-500/20 bg-emerald-500/[0.03]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <h3 className="font-semibold text-sm">Constitutional Commerce Attestation</h3>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="success" className="gap-1.5 text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Phase 2B
            </Badge>
            <Badge variant="info" className="text-xs">LIVE</Badge>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4">
          {hasliveAttestation ? (
            <>
              {/* governance_hash */}
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                  governance_hash
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-xs text-emerald-300 bg-zinc-950/60
                                   border border-zinc-800 rounded-md px-3 py-2 truncate">
                    {latestAttestation.governanceHash}
                  </code>
                  <span className="shrink-0 flex items-center gap-1 text-xs text-emerald-400 font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5" />VERIFIED
                  </span>
                </div>
              </div>

              {/* algorand_tx_id */}
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                  Algorand TX
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-xs text-violet-300 bg-zinc-950/60
                                   border border-zinc-800 rounded-md px-3 py-2 truncate">
                    {latestAttestation.algorand_tx_id}
                  </code>
                  <span className="shrink-0 flex items-center gap-1 text-xs text-emerald-400 font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5" />FINAL
                  </span>
                </div>
              </div>
            </>
          ) : (
            /* Awaiting first x402 transaction */
            <div className="flex flex-col items-center justify-center py-4 text-center gap-2">
              <div className="w-10 h-10 rounded-full bg-zinc-800/50 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-zinc-600" />
              </div>
              <p className="text-sm text-zinc-500 font-medium">Awaiting first x402 transaction</p>
              <p className="text-xs text-zinc-600 max-w-xs">
                Constitutional Commerce attestation will appear here once an AI agent
                completes a payment at <span className="text-zinc-400 font-mono">elintra.algo</span>
              </p>
            </div>
          )}

          {/* Metadata row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-1 border-t border-zinc-800/60">
            <div>
              <p className="text-xs text-zinc-600 mb-0.5">Settlement</p>
              <p className="text-xs font-mono text-zinc-300 font-semibold">elintra.algo</p>
            </div>
            <div>
              <p className="text-xs text-zinc-600 mb-0.5">Facilitator</p>
              <p className="text-xs font-mono text-zinc-300">GoPlausible</p>
            </div>
            <div>
              <p className="text-xs text-zinc-600 mb-0.5">Finality</p>
              <p className="text-xs font-mono text-emerald-400 font-semibold">4s FINAL</p>
            </div>
            <div>
              <p className="text-xs text-zinc-600 mb-0.5">x402 Payments</p>
              <p className="text-xs font-mono text-violet-400 font-semibold">
                {x402PaymentCount.toLocaleString()}
              </p>
            </div>
          </div>

          {/* USDT settled + explorer link */}
          <div className="flex items-center justify-between pt-1">
            <div>
              <span className="text-xs text-zinc-500">USDT Settled via Algorand: </span>
              <span className="text-sm font-mono font-bold text-emerald-400">
                ${algoSettledUsdt}
              </span>
            </div>
            <a
              href="https://algoexplorer.io/address/elintra.algo"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300
                         transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              View on Algorand
            </a>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
